"use client";

/**
 * Small popover shown below the hero date when a public visitor clicks it.
 * Renders the canonical calendar-provider list (Google / Outlook / .ics).
 * Click-outside (or Escape) closes the popover.
 */

import { Fragment, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import type React from "react";
import { getCalendarOptions, type CalendarEventInput } from "@/lib/utils/calendar-links";

interface HeroCalendarDropdownProps {
  id: string;
  event: CalendarEventInput;
  /** Ref to the container element (button + dropdown). Used to detect outside clicks. */
  containerRef: RefObject<HTMLDivElement | null>;
  /** Ref to the trigger button so focus can return to it on close. */
  triggerRef: RefObject<HTMLButtonElement | null>;
  onClose: () => void;
}

/**
 * Calendar add-link popover for the hero date.
 *
 * @param props - Event details, refs, and close callback.
 * @returns Absolutely-positioned dropdown panel.
 */
export default function HeroCalendarDropdown({
  id,
  event,
  containerRef,
  triggerRef,
  onClose,
}: HeroCalendarDropdownProps): React.ReactElement | null {
  // Refs
  const panelRef = useRef<HTMLDivElement>(null);

  // States
  const [isMounted, setIsMounted] = useState(false);

  // Derived values
  const options = useMemo(() => getCalendarOptions(event), [event]);

  // Effects
  useEffect(() => {
    // `pointerdown` covers mouse + touch + pen in one listener. The previous `mousedown`-only
    // version missed iOS Safari taps on non-interactive backgrounds (plain divs/text without
    // a click handler or cursor: pointer), which left the dropdown stuck open.
    function handlePointerDown(e: PointerEvent) {
      if (containerRef.current?.contains(e.target as Node)) return;
      onClose();
      // Mirror the Escape path's focus return so the keyboard user lands back on the trigger
      // (mouse users naturally take focus from their click target; the guard is cheap).
      triggerRef.current?.focus();
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      // Only act when focus is inside our popover, so the listener doesn't yank focus while
      // the user is dismissing an unrelated modal/popover elsewhere on the page.
      if (!containerRef.current?.contains(document.activeElement)) return;
      onClose();
      triggerRef.current?.focus();
    }
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [containerRef, triggerRef, onClose]);

  useEffect(() => {
    const rafHandle = requestAnimationFrame(() => {
      setIsMounted(true);
      panelRef.current?.querySelector<HTMLElement>("a, button")?.focus();
    });
    return () => cancelAnimationFrame(rafHandle);
  }, []);

  // Modifier/aux clicks (cmd, ctrl, shift, alt, middle-button) signal "open in new tab"
  // — preserve the dropdown so the user can pick another provider without re-opening it.
  function handleLinkClick(e: React.MouseEvent<HTMLAnchorElement>): void {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (e.button !== 0) return;
    onClose();
  }

  if (!event.date) return null;

  // Render
  const itemClass =
    "block w-full px-4 py-3 text-left text-sm font-semibold text-white transition-colors hover:bg-white/10 focus-visible:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/60";

  return (
    <div
      id={id}
      ref={panelRef}
      aria-label="Add to calendar"
      className={`absolute left-1/2 top-full z-50 mt-2 w-52 origin-top -translate-x-1/2 overflow-hidden rounded-xl border border-white/10 bg-gray-900/95 shadow-lg backdrop-blur-md motion-safe:transition motion-safe:duration-150 motion-safe:ease-out ${
        isMounted
          ? "motion-safe:translate-y-0 motion-safe:scale-100 motion-safe:opacity-100"
          : "motion-safe:-translate-y-1 motion-safe:scale-95 motion-safe:opacity-0"
      }`}
    >
      {options.map((opt, i) => (
        <Fragment key={opt.key}>
          {i > 0 && <div className="mx-4 border-t border-white/10" />}
          {opt.kind === "link" ? (
            <a
              href={opt.href}
              target="_blank"
              rel="noopener noreferrer"
              className={itemClass}
              onClick={handleLinkClick}
            >
              {opt.label}
            </a>
          ) : (
            <button
              type="button"
              onClick={() => {
                opt.onClick();
                onClose();
              }}
              className={itemClass}
            >
              {opt.label}
            </button>
          )}
        </Fragment>
      ))}
    </div>
  );
}
